import { Box, Button, useTheme } from "@mui/material";
import { FC } from "react";
import { BORDER_RADIUS } from "../constants";

interface YearsGridProps {
  earliestStartYear: number;
  startDate: Date | null;
  endDate: Date | null;
  onYearSelect: (year: number) => void;
}

export const YearsGrid: FC<YearsGridProps> = ({
  earliestStartYear,
  startDate,
  endDate,
  onYearSelect,
}) => {
  const theme = useTheme();

  const years = Array.from({ length: 4 }, (_, i) => earliestStartYear + i);

  const isInRange = (year: number) => {
    if (!startDate) return false;

    if (!endDate) {
      return year === startDate.getUTCFullYear();
    }

    const startYear = startDate.getUTCFullYear();
    const endYear = endDate.getUTCFullYear();

    return year >= startYear && year <= endYear;
  };

  const getBorderRadius = (year: number) => {
    if (!isInRange(year)) return BORDER_RADIUS;

    const startYear = startDate ? startDate.getUTCFullYear() : null;
    const endYear = endDate ? endDate.getUTCFullYear() : startYear;

    if (!startYear || !endYear) return 0;

    const isFirst = year === startYear;
    const isLast = year === endYear;

    if (isFirst && isLast) {
      return BORDER_RADIUS;
    } else if (isFirst) {
      return ` ${BORDER_RADIUS} 0 0 ${BORDER_RADIUS}`;
    } else if (isLast) {
      return `0 ${BORDER_RADIUS} ${BORDER_RADIUS} 0`;
    }

    return 0;
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        rowGap: 1,
        p: 2,
      }}
    >
      {years.map((year) => (
        <Button
          key={year}
          onClick={() => onYearSelect(year)}
          sx={{
            backgroundColor: isInRange(year)
              ? theme.colors.primary[5]
              : "transparent",
            borderRadius: getBorderRadius(year),
            color: isInRange(year)
              ? theme.colors.defaultBackground
              : theme.colors.text[5],
            fontSize: 12,
            fontWeight: 500,
            height: 140,
            transition: "none",
            "&:hover": {
              backgroundColor: isInRange(year)
                ? theme.colors.primary[6]
                : theme.colors.grey,
              transition: isInRange(year) ? "none" : "background-color 0.1s",
            },
          }}
        >
          {year}
        </Button>
      ))}
    </Box>
  );
};
